/**
 * layoutPresets — save/apply named layout snapshots (panel heights +
 * collapse state + compact preset).
 *
 * Persistence:
 *   • Primary: MongoDB via `/api/users/me/layout-presets`
 *     (cross-device sync; up to 24 presets per user).
 *   • Fallback: localStorage('apexflow.layout.presets') — keeps the
 *     feature working offline / before the user is authenticated, and
 *     mirrors the server bag once we successfully fetch.
 *
 * Public API (all async where needed):
 *   listPresets()            → [{name, ts}]   — local view
 *   savePreset(name)         → name           — local + server PUT
 *   deletePreset(name)       → bool           — local + server DELETE
 *   applyPreset(name)        → bool           — clears + writes locally
 *   syncFromServer()         → number         — refreshes local cache
 */
import { api } from '@/lib/api';

const PRESETS_KEY = 'apexflow.layout.presets';
const HEIGHT_PREFIX = 'apexflow.height.';
const PANEL_PREFIX = 'apexflow.panel.';
const PRESET_KEY = 'apexflow.dashboard.preset';
const LAST_LOCAL_KEY = 'apexflow.layout.lastLocal';
const LAST_SYNC_KEY = 'apexflow.layout.lastSync';

// Sync-state status: 'synced' | 'syncing' | 'stale' | 'unknown'
let _status = 'unknown';
const _listeners = new Set();
function _emit(s) {
  _status = s;
  _listeners.forEach((fn) => { try { fn(s); } catch (_) { /* */ } });
}
export function subscribeSyncStatus(fn) {
  _listeners.add(fn);
  fn(_status);
  return () => _listeners.delete(fn);
}
export function getSyncSnapshot() {
  let local = 0, sync = 0;
  try {
    local = parseInt(localStorage.getItem(LAST_LOCAL_KEY) || '0', 10) || 0;
    sync = parseInt(localStorage.getItem(LAST_SYNC_KEY) || '0', 10) || 0;
  } catch (_) { /* */ }
  return { status: _status, lastLocal: local, lastSync: sync };
}
function _markLocal() {
  try { localStorage.setItem(LAST_LOCAL_KEY, String(Date.now())); }
  catch (_) { /* */ }
}
function _markSync() {
  try { localStorage.setItem(LAST_SYNC_KEY, String(Date.now())); }
  catch (_) { /* */ }
}

function _readLocal() {
  try {
    const raw = localStorage.getItem(PRESETS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function _writeLocal(obj) {
  try { localStorage.setItem(PRESETS_KEY, JSON.stringify(obj)); }
  catch (_) { /* quota */ }
}

function _isAuthed() {
  try {
    return Boolean(localStorage.getItem('apex_token')
                   || localStorage.getItem('apexflow_token')
                   || document.cookie.includes('session_token'));
  } catch { return false; }
}

async function _putAllToServer(bag) {
  if (!_isAuthed()) {
    _emit('stale');
    return false;
  }
  _emit('syncing');
  try {
    await api.put('/users/me/layout-presets', { presets: bag });
    _markSync();
    _emit('synced');
    return true;
  } catch (e) {
    _emit('stale');
    return false;
  }
}

export function listPresets() {
  const all = _readLocal();
  return Object.keys(all)
    .map((name) => ({ name, ts: all[name]?.ts || 0 }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function snapshotCurrent() {
  const heights = {};
  const panels = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith(HEIGHT_PREFIX)) {
        heights[k] = localStorage.getItem(k);
      } else if (k.startsWith(PANEL_PREFIX)) {
        panels[k] = localStorage.getItem(k);
      }
    }
  } catch (_) { /* ignore */ }
  let preset = null;
  try { preset = localStorage.getItem(PRESET_KEY); } catch (_) { /* */ }
  return { heights, panels, preset };
}

export async function savePreset(name) {
  const trimmed = String(name || '').trim();
  if (!trimmed) throw new Error('preset name required');
  if (trimmed.length > 60) throw new Error('preset name too long (60 max)');
  const all = _readLocal();
  if (!(trimmed in all) && Object.keys(all).length >= 24) {
    throw new Error('max 24 presets — delete one to add another');
  }
  all[trimmed] = { ts: Date.now(), data: snapshotCurrent() };
  _writeLocal(all);
  _markLocal();
  await _putAllToServer(all);
  return trimmed;
}

export async function deletePreset(name) {
  const all = _readLocal();
  if (!(name in all)) return false;
  delete all[name];
  _writeLocal(all);
  _markLocal();
  if (_isAuthed()) {
    _emit('syncing');
    try {
      await api.delete(`/users/me/layout-presets/${encodeURIComponent(name)}`);
      _markSync();
      _emit('synced');
    } catch (_) {
      _emit('stale');
    }
  } else {
    _emit('stale');
  }
  return true;
}

export function applyPreset(name) {
  const all = _readLocal();
  const p = all[name];
  if (!p) return false;
  const data = p.data || {};
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith(HEIGHT_PREFIX) || k.startsWith(PANEL_PREFIX)) {
        toRemove.push(k);
      }
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
    Object.entries(data.heights || {}).forEach(([k, v]) =>
      localStorage.setItem(k, String(v)));
    Object.entries(data.panels || {}).forEach(([k, v]) =>
      localStorage.setItem(k, String(v)));
    if (data.preset) localStorage.setItem(PRESET_KEY, String(data.preset));
    else localStorage.removeItem(PRESET_KEY);
  } catch (_) { /* ignore */ }
  return true;
}

/**
 * Pull the server bag and merge it with the local one (server wins on
 * collisions — server is the source of truth for cross-device sync).
 * Returns the merged count.
 */
export async function syncFromServer() {
  if (!_isAuthed()) { _emit('stale'); return 0; }
  _emit('syncing');
  try {
    const { data } = await api.get('/users/me/layout-presets');
    const remote = data?.presets || {};
    const local = _readLocal();
    const merged = { ...local, ...remote };
    _writeLocal(merged);
    _markSync();
    // If a local mutation hasn't been pushed yet, mark stale.
    let lastLocal = 0, lastSync = 0;
    try {
      lastLocal = parseInt(localStorage.getItem(LAST_LOCAL_KEY) || '0', 10) || 0;
      lastSync = parseInt(localStorage.getItem(LAST_SYNC_KEY) || '0', 10) || 0;
    } catch (_) { /* */ }
    _emit(lastLocal > lastSync ? 'stale' : 'synced');
    return Object.keys(merged).length;
  } catch (_) {
    _emit('stale');
    return Object.keys(_readLocal()).length;
  }
}
